import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {Injectable} from '@angular/core';
import {Link} from './link';
import {BehaviorSubject} from 'rxjs';
import {CrupdateLinkPayload} from './link.service';
import {Settings} from '@common/core/config/settings.service';
import { LinkGroup } from './link-group';

interface LinkErrors extends Partial<Link> {
    long_urls?: string;
}

@Injectable({
    providedIn: 'root',
})
export class LinkForm {
    formGroup: FormGroup;
    errors$ = new BehaviorSubject<LinkErrors>({});

    geoRules: FormArray;
    deviceRules: FormArray;
    platformRules: FormArray;
    expClicksRule: FormGroup;

    linkHash: FormControl;

    constructor(private fb: FormBuilder, private settings: Settings) {
        this.build();
        this.linkHash = this.formGroup.get('hash') as FormControl;
    }

    value(): CrupdateLinkPayload {
        const payload = {...this.formGroup.value};

        // merge device / country rules and remove empty rules
        payload.rules = (payload.geo_rules || [])
            .concat(payload.device_rules)
            .concat(payload.platform_rules)
            .filter(rule => rule?.key && rule?.value);

        // delete geo and device rule fields
        delete payload.geo_rules;
        delete payload.device_rules;
        delete payload.platform_rules;

        // split link type into type and type id values
        if (payload.type.indexOf(':') > -1) {
            payload.type_id = parseInt(payload.type.split(':')[1]);
            payload.type = payload.type.split(':')[0];
        }

        if (payload.long_urls) {
            payload.long_urls = payload.long_urls.split(/\n/g);
            // send only single url or multiple urls
            delete payload.long_url;
            delete payload.alias;
        } else {
            // make sure validation is not triggered with empty string
            delete payload.long_urls;
        }

        // don't unset password if user did not make any changes to it
        if (!this.formGroup.get('password').dirty) {
            delete payload.password;
        }

        return payload;
    }

    private build() {
        const defaultType = this.settings.get('links.default_type', 'direct');
        this.formGroup = this.fb.group({
            type: [defaultType],
            name: [''],
            long_url: [''],
            long_urls: [''],
            domain_id: [null],
            hash: [''],
            alias: [''],
            image: [''],
            utm: [''],
            password: [''],
            active: [true],
            expires_at: [''],
            activates_at: [''],
            description: [''],
            tags: [[]],
            pixels: [[]],
            groups: [[]],
            geo_rules: this.fb.array([]),
            device_rules: this.fb.array([]),
            platform_rules: this.fb.array([]),
            exp_clicks_rule: this.fb.group({
                key: [''],
                value: [''],
            })
        });

        this.geoRules = this.formGroup.controls.geo_rules as FormArray;
        this.deviceRules = this.formGroup.controls.device_rules as FormArray;
        this.platformRules = this.formGroup.controls.platform_rules as FormArray;
        this.expClicksRule = this.formGroup.controls.exp_clicks_rule as FormGroup;

        this.addNewGeoRule();
        this.addNewDeviceRule();
        this.addNewPlatformRule();

        this.formGroup.get('long_urls').valueChanges.subscribe(value => {
            if (value) {
                this.formGroup.get('alias').disable();
            } else {
                this.formGroup.get('alias').enable();
            }
        });
    }

    patchValue(link?: Link|LinkGroup) {
        if (link) {
            const value = {...link} as {[key: string]: any};
            // type=overlay, type_id=1 => type=overlay:1
            if ('type_id' in link && link.type_id) {
                value.type = `${link.type}:${link.type_id}`;
            }
            const rules = link.rules || [];
            value.geo_rules = rules.filter(r => r.type === 'geo');
            value.device_rules = rules.filter(r => r.type === 'device');
            value.platform_rules = rules.filter(r => r.type === 'platform');
            value.exp_clicks_rule = rules.find(r => r.type === 'exp_clicks');
            value.tags = (value.tags || []).map(t => t.name);
            value.pixels = (value.pixels || []).map(p => p.id);
            value.groups = (value.groups || []).map(p => p.id);

            // show an indication to user that password exists
            // even though we can't hydrate actual password
            if (link.has_password) {
                value.password = '******';
            }

            this.formGroup.patchValue(value);

            if (value.geo_rules.length) {
                this.geoRules.clear();
                value.geo_rules.forEach(rule => {
                    this.addNewGeoRule(rule.key, rule.value);
                });
            }
            if (value.device_rules.length) {
                this.deviceRules.clear();
                value.device_rules.forEach(rule => {
                    this.addNewDeviceRule(rule.key, rule.value);
                });
            }
            if (value.platform_rules.length) {
                this.platformRules.clear();
                value.platform_rules.forEach(rule => {
                    this.addNewPlatformRule(rule.key, rule.value);
                });
            }
        }
    }

    addNewGeoRule(key: string = 'us', value: string = '') {
        this.geoRules.push(this.fb.group({key: [key], value: [value], type: 'geo'}));
    }

    addNewDeviceRule(key: string = 'mobile', value: string = '') {
        this.deviceRules.push(this.fb.group({key: [key], value: [value], type: 'device'}));
    }

    addNewPlatformRule(key: string = 'windows', value: string = '') {
        this.platformRules.push(this.fb.group({key: [key], value: [value], type: 'platform'}));
    }

    setErrors(messages?: LinkErrors) {
        this.errors$.next(messages || {});
    }
}
